import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import GrantitudeSpiral from './GrantitudeSpiral';
import PersistentFooter from './PersistentFooter';
import { Key, Shield, Sparkles, Menu, X, Crown } from 'lucide-react';
import globalHeartbeat from '../utils/globalHeartbeatAudio';

const CosmicLandingPage = ({ onNameSubmission, onNavigateToAuth, onNavigateToMessaging, onNavigateToProjects, onNavigateToReinn }) => {
  const [playerName, setPlayerName] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [orbPhase, setOrbPhase] = useState(0);
  const [joinWaitlistBlink, setJoinWaitlistBlink] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);

  // Sacred animation cycles - 50% slower
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setOrbPhase(prev => (prev + 0.125) % 360);
    }, 100);
    
    // Enhanced Safari audio support with force initialization
    if (window.planetaryFedHeartbeat) {
      window.planetaryFedHeartbeat.setEnabled(true);
      
      // Force audio context activation
      const enableAudio = () => {
        if (window.planetaryFedHeartbeat.audioContext?.state === 'suspended') {
          window.planetaryFedHeartbeat.audioContext.resume().then(() => {
            console.log('🎵 AUDIO: Context resumed successfully');
            window.planetaryFedHeartbeat.forceRestart();
          }).catch(() => {
            console.log('🎵 AUDIO: Requires user interaction');
          });
        } else {
          window.planetaryFedHeartbeat.forceRestart();
          console.log('🎵 AUDIO: Heartbeat force restarted');
        }
      };
      
      // Multiple interaction triggers for audio activation
      document.addEventListener('click', enableAudio, { once: true });
      document.addEventListener('touchstart', enableAudio, { once: true });
      document.addEventListener('keydown', enableAudio, { once: true });
      
      // Auto-attempt after delay with enhanced debugging
      setTimeout(() => {
        console.log('🎵 AUDIO DEBUG:', {
          heartbeat_exists: !!window.planetaryFedHeartbeat,
          heartbeat_active: window.planetaryFedHeartbeat?.heartbeatActive,
          audio_context_state: window.planetaryFedHeartbeat?.audioContext?.state,
          enabled: window.planetaryFedHeartbeat?.enabled
        });
        
        if (window.planetaryFedHeartbeat && !window.planetaryFedHeartbeat.heartbeatActive) {
          console.log('🎵 FORCE START: Attempting to start heartbeat...');
          enableAudio();
          
          // Additional force start attempt
          setTimeout(() => {
            if (window.planetaryFedHeartbeat && window.planetaryFedHeartbeat.enabled && !window.planetaryFedHeartbeat.heartbeatActive) {
              window.planetaryFedHeartbeat.startHeartbeat();
              console.log('🎵 FORCE START: Heartbeat manually started');
            }
          }, 1000);
        }
      }, 2000);
    }
    
    return () => clearInterval(spiralInterval);
  }, []);

  // Waitlist button blinking
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setJoinWaitlistBlink(prev => !prev);
    }, 2000);
    
    return () => clearInterval(blinkInterval);
  }, []);

  // Subtle cosmic distortion effects
  useEffect(() => {
    const consciousnessShift = () => {
      if (Math.random() > 0.96) {
        document.body.style.filter = `hue-rotate(${Math.random() * 5}deg) brightness(${1 + Math.random() * 0.05})`;
        setTimeout(() => {
          document.body.style.filter = 'none';
        }, 150);
      }
    };
    
    const consciousnessInterval = setInterval(consciousnessShift, 4000);
    return () => clearInterval(consciousnessInterval);
  }, []);

  const handleNameEntry = () => {
    // Always go to Sacred Tenets onboarding - no invite code required on landing page
    onNameSubmission('Timeline Explorer');
  };

  const confirmAccess = () => {
    // Simplified - always go to Sacred Tenets
    document.body.classList.add('consciousness-breakthrough');
    
    setTimeout(() => {
      document.body.classList.remove('consciousness-breakthrough');
      onNameSubmission('Timeline Explorer');
    }, 3000);
  };

  const handleJoinWaitlist = () => {
    // Join waitlist also goes to Sacred Tenets onboarding
    onNameSubmission('Waitlist Seeker');
  };

  const toggleAudio = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (window.planetaryFedHeartbeat) {
      const newState = !audioEnabled;
      setAudioEnabled(newState);
      
      if (newState) {
        window.planetaryFedHeartbeat.setEnabled(true);
        
        // Enhanced Safari support
        if (window.planetaryFedHeartbeat.audioContext?.state === 'suspended') {
          window.planetaryFedHeartbeat.audioContext.resume().then(() => {
            window.planetaryFedHeartbeat.forceRestart();
            console.log('🎵 SAFARI: Heartbeat enabled and started');
          });
        } else {
          window.planetaryFedHeartbeat.forceRestart();
          console.log('🎵 AUDIO: Heartbeat enabled');
        }
      } else {
        window.planetaryFedHeartbeat.setEnabled(false);
        console.log('🔇 AUDIO: Heartbeat disabled');
      }
    }
  };

  return (
    <>
      {/* CSS for effects and tan/paper text color */}
      <style jsx>{`
        .consciousness-breakthrough {
          animation: consciousness-awakening 3s ease-in-out;
        }
        
        @keyframes consciousness-awakening {
          0% { filter: none; transform: none; }
          15% { filter: hue-rotate(180deg) contrast(200%) brightness(150%); transform: scale(1.01) rotate(0.5deg); }
          30% { filter: invert(1) sepia(1) saturate(5) hue-rotate(290deg); transform: scale(0.99) rotate(-0.5deg); }
          50% { filter: blur(1px) brightness(300%) contrast(150%); transform: scale(1.02) rotate(0deg); }
          75% { filter: hue-rotate(320deg) brightness(200%) contrast(300%); transform: scale(0.98) rotate(0.3deg); }
          90% { filter: brightness(150%) contrast(120%); transform: scale(1.005) rotate(-0.2deg); }
          100% { filter: none; transform: none; }
        }
        
        .sacred-pulse {
          animation: sacred-pulse 4s ease-in-out infinite;
        }
        
        @keyframes sacred-pulse {
          0%, 100% { 
            text-shadow: 0 0 30px rgba(210, 180, 140, 0.9), 0 0 60px rgba(222, 184, 135, 0.6);
            transform: scale(1);
          }
          50% { 
            text-shadow: 0 0 40px rgba(210, 180, 140, 1), 0 0 80px rgba(222, 184, 135, 0.8), 0 0 120px rgba(245, 222, 179, 0.4);
            transform: scale(1.02);
          }
        }
        
        .paper-text {
          color: #D2B48C;
          text-shadow: 0 0 30px rgba(210, 180, 140, 0.9), 0 0 60px rgba(222, 184, 135, 0.6);
        }
        
        .blink {
          animation: sacred-blink 2s infinite;
        }
        
        @keyframes sacred-blink {
          0%, 50% { opacity: 1; }
          25%, 75% { opacity: 0.3; }
        }
      `}</style>

      <div className="min-h-screen relative overflow-hidden">
        {/* Background image layer with transparency */}
        <div 
          className="fixed inset-0 pointer-events-none" 
          style={{ 
            zIndex: 0,
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_remedy-council/artifacts/kyj6r6dp_ChatGPT%20Image%20Oct%208%2C%202025%20at%2007_52_26%20PM.jpeg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.6
          }}
        />
        
        {/* Grant spiral background - more prominent */}
        <div className="fixed inset-0 pointer-events-none opacity-30" style={{ zIndex: 1 }}>
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{
              transform: `scale(10) rotate(${orbPhase * 0.4}deg)`,
              filter: 'blur(4px) saturate(1.8) brightness(1.5)',
              mixBlendMode: 'screen'
            }}
          >
            <GrantitudeSpiral />
          </div>
        </div>

        {/* Movie-Title Unit - Much Higher Positioning */}
        <div className="absolute top-16 left-0 right-0 z-20 flex justify-center">
          <div className="text-center max-w-4xl px-4">
            
            {/* Main Title Unit */}
            <div className="mb-8">
              <h1 
                className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-wider"
                style={{
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 50px rgba(210, 180, 140, 1.2), 0 0 100px rgba(222, 184, 135, 0.9), 0 0 150px rgba(255, 215, 0, 0.4)',
                  letterSpacing: '0.08em',
                  color: '#E6C794',
                  filter: 'brightness(1.3)'
                }}
              >
                <span style={{ fontSize: '1.2em' }}>SEEDY PEEPS</span>
              </h1>
              
              {/* Subtitle lines */}
              <p 
                className="text-lg md:text-2xl font-bold tracking-wide mt-4"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 20px rgba(210, 180, 140, 1.0)',
                  letterSpacing: '0.1em',
                  color: '#E6C794',
                  filter: 'brightness(1.3)'
                }}
              >
                a guild to build belonging
              </p>
              
              <p 
                className="text-base md:text-xl font-bold tracking-wide mt-2"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 25px rgba(210, 180, 140, 1.1)',
                  letterSpacing: '0.15em',
                  color: '#E6C794',
                  filter: 'brightness(1.3)'
                }}
              >
                with logistics and song
              </p>
            </div>
            
            {/* Subtitle Section */}
            <div className="space-y-4 mt-12">
              <p 
                className="text-base md:text-lg font-normal tracking-wide leading-relaxed"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 20px rgba(210, 180, 140, 1.1), 0 0 40px rgba(222, 184, 135, 0.8)',
                  maxWidth: '800px',
                  margin: '0 auto',
                  letterSpacing: '0.05em',
                  color: '#E6C794',
                  filter: 'brightness(1.3)'
                }}
              >
                we are a private planetary federation of families, seeking bliss, tech, and mastery, to help us get things done!
              </p>
            </div>
          </div>
        </div>

        {/* ELEGANT FLOATING TEXT - No boxes, pure cosmic background */}
        
        {/* Call-to-Action - by invitation only */}
        <div 
          className="absolute inset-x-0 z-20"
          style={{ bottom: '180px' }}
        >
          <div className="flex justify-center px-4">
            <div className="max-w-2xl w-full text-center">
              <div className="space-y-2">
                <p 
                  className="text-sm md:text-base font-light tracking-wide italic"
                  style={{
                    fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                    textShadow: '0 0 18px rgba(210, 180, 140, 0.9)',
                    letterSpacing: '0.1em',
                    color: '#D2B48C',
                    fontSize: '0.9rem',
                    filter: 'brightness(1.2)'
                  }}
                >
                  by invitation only
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Planetary Fed Branding - Bottom Right */}
        <div className="absolute bottom-4 right-4 z-20">
          <a
            href="https://planetaryfed.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/40 text-xs font-light italic hover:text-white/60 transition-colors cursor-pointer"
            style={{ 
              fontFamily: '"Crimson Text", "Libre Baskerville", serif',
              textShadow: '0 0 8px rgba(255, 255, 255, 0.2)'
            }}
          >
            brought to you by Planetary Fed
          </a>
        </div>

        {/* Duplicate content removed */}

        {/* Ultra Minimal Horizontal Footer */}
        <div className="absolute bottom-0 left-0 right-0 z-30">
          <div className="bg-black/60 backdrop-blur-sm border-t border-white/10">
            <div className="max-w-6xl mx-auto px-4 py-1">
              
              {/* Single Horizontal Row */}
              <div className="flex items-center justify-between text-xs">
                
                {/* Left: Minimal Branding - All clickable */}
                <div className="flex items-center space-x-4">
                  <button 
                    onClick={() => onNavigateToReinn ? onNavigateToReinn() : console.log('Navigate to re-Inn')}
                    className="text-yellow-200 font-medium text-sm hover:text-yellow-100 transition-all" 
                    style={{ fontFamily: '"Crimson Text", serif' }}
                  >
                    re-Inn
                  </button>
                  <button 
                    onClick={() => onNavigateToReinn ? onNavigateToReinn() : console.log('Navigate to re-Inn')}
                    className="text-amber-300 hover:text-amber-200 transition-all"
                  >
                    Family Federation
                  </button>
                  <button 
                    onClick={() => onNavigateToReinn ? onNavigateToReinn() : console.log('Navigate to re-Inn')}
                    className="text-amber-300 hidden md:inline hover:text-amber-200 transition-all"
                  >
                    AirBNB for Care
                  </button>
                </div>
                
                {/* Center: Action Buttons - 50% smaller */}
                <div className="flex gap-1.5">
                  <button
                    onClick={handleNameEntry}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white h-5 px-2 rounded text-[9px] transition-all hover:scale-105"
                  >
                    Secret History Game
                  </button>
                  
                  <button
                    onClick={() => onNavigateToProjects && onNavigateToProjects()}
                    className="bg-blue-600 hover:bg-blue-500 text-white h-5 px-2 rounded text-[9px] transition-all hover:scale-105"
                  >
                    Projects
                  </button>
                  
                  <button
                    onClick={() => onNavigateToReinn ? onNavigateToReinn() : console.log('Navigate to re-Inn')}
                    className="bg-amber-500 hover:bg-amber-400 text-black h-5 px-2 rounded text-[9px] transition-all hover:scale-105"
                  >
                    re-Inn Stays
                  </button>
                </div>
                
                {/* Right: Services - All clickable */}
                <div className="text-right hidden md:block">
                  <button 
                    onClick={() => onNavigateToReinn ? onNavigateToReinn() : console.log('Navigate to re-Inn')}
                    className="text-white/60 hover:text-white/80 transition-all"
                  >
                    Sacred Retreats • Family Archives • Remedy Council
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Join Waitlist and Code Entry - 50% smaller */}
        <div 
          className="absolute inset-x-0 z-20"
          style={{ bottom: '80px' }}
        >
          <div className="flex flex-col items-center gap-2">
            <Button 
              onClick={handleJoinWaitlist}
              className={`bg-gradient-to-r from-yellow-600/80 to-orange-600/80 hover:from-yellow-500/90 hover:to-orange-500/90 text-white px-3 py-1.5 text-xs font-medium tracking-wide shadow-xl transition-all duration-500 backdrop-blur-xl border border-white/40 hover:border-white/60 ${joinWaitlistBlink ? 'blink' : ''}`}
              style={{
                boxShadow: '0 0 20px rgba(255, 215, 0, 0.6), 0 0 40px rgba(255, 165, 0, 0.3)',
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 10px rgba(255, 255, 255, 0.8)'
              }}
            >
              <Sparkles className="w-3 h-3 mr-1.5" />
              Join Waitlist
            </Button>
            
            <button
              onClick={handleNameEntry}
              className="text-white/70 hover:text-white/90 text-[10px] underline transition-colors"
              style={{
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 10px rgba(210, 180, 140, 0.6)',
                letterSpacing: '0.05em'
              }}
            >
              Click here if you have a code
            </button>
          </div>
        </div>
        {/* Persistent Footer */}
        <PersistentFooter />
        
        {/* Anatomical Heart Toggle - Landing Page */}
        <div className="fixed z-50" style={{ bottom: '12px', left: '24px' }}>
          <button
            onClick={() => {
              const newAudioState = !audioEnabled;
              setAudioEnabled(newAudioState);
              
              if (window.planetaryFedHeartbeat) {
                window.planetaryFedHeartbeat.setEnabled(newAudioState);
                if (newAudioState) {
                  window.planetaryFedHeartbeat.forceRestart();
                }
              }
            }}
            className={`text-lg hover:scale-125 transition-all duration-300 p-1 border-none outline-none opacity-90 hover:opacity-100 ${
              audioEnabled ? 'animate-pulse' : ''
            }`}
            style={{ 
              background: 'transparent',
              border: 'none',
              filter: audioEnabled 
                ? 'drop-shadow(0 0 6px rgba(255, 20, 147, 0.8)) drop-shadow(0 0 12px rgba(255, 105, 180, 0.6))'
                : 'drop-shadow(0 0 4px rgba(0, 0, 0, 0.8))',
              animation: audioEnabled ? 'anatomical-heartbeat 1.2s ease-in-out infinite' : 'none'
            }}
            title={`${audioEnabled ? 'Mute all audio' : 'Enable all audio'}`}
          >
            <span style={{ 
              filter: audioEnabled ? 'brightness(1.2) saturate(1.4)' : 'brightness(0.6)',
              fontSize: '0.8em',
              display: 'inline-block',
              animation: audioEnabled ? 'anatomical-heartbeat 1.2s ease-in-out infinite' : 'none'
            }}>
              🫀
            </span>
          </button>
          
          {audioEnabled && (
            <div className="absolute -top-0.5 -right-0.5">
              <div className="w-2 h-2 bg-red-400 rounded-full animate-ping opacity-75"></div>
              <div className="absolute top-0 left-0 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            </div>
          )}
          
          {/* Anatomical heartbeat animation */}
          <style jsx>{`
            @keyframes anatomical-heartbeat {
              0%, 100% { 
                transform: scale(1.0); 
                filter: brightness(1.2) saturate(1.4) drop-shadow(0 0 6px rgba(255, 20, 147, 0.6));
              }
              15% { 
                transform: scale(1.2); 
                filter: brightness(1.4) saturate(1.7) drop-shadow(0 0 12px rgba(255, 20, 147, 0.9));
              }
              30% { 
                transform: scale(1.05); 
                filter: brightness(1.25) saturate(1.5) drop-shadow(0 0 8px rgba(255, 20, 147, 0.7));
              }
              45% { 
                transform: scale(1.15); 
                filter: brightness(1.35) saturate(1.6) drop-shadow(0 0 10px rgba(255, 20, 147, 0.8));
              }
              60% { 
                transform: scale(1.0); 
                filter: brightness(1.2) saturate(1.4) drop-shadow(0 0 6px rgba(255, 20, 147, 0.6));
              }
            }
          `}</style>
        </div>
      </div>
    </>
  );
};

export default CosmicLandingPage;